import { type CSSProperties, useEffect, useState } from "react";

const WORDMARK = "irfan syafi";

/** How long the wordmark holds before lifting. */
const HOLD_MS = 1100;
/** Must match the .curtain transition duration in index.css (--dur-enter). */
const LIFT_MS = 750;

const SEEN_KEY = "persona.curtain-seen";

function shouldSkip(): boolean {
  if (typeof window === "undefined") return true;

  // Reduced motion: the curtain is pure atmosphere, so it is skipped entirely
  // rather than played instantly. An instant flash of near-black is worse than
  // no curtain at all for anyone who asked for less movement.
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return true;

  // Once per tab. A page you land on once can afford a moment of theatre; a page
  // you return to after sending a message cannot, and this app is the second
  // kind. sessionStorage rather than localStorage so it does come back on a
  // genuinely fresh visit.
  try {
    if (sessionStorage.getItem(SEEN_KEY)) return true;
    sessionStorage.setItem(SEEN_KEY, "1");
  } catch {
    // Storage blocked — show it. Erring toward the designed experience is
    // right here, because the cost of a false positive is one extra second.
  }

  return false;
}

/**
 * The page-load curtain, adapted from irfansyafi.com.
 *
 * The portfolio holds its curtain for about five seconds. That reads as
 * confident on a page you scroll through; on a page whose whole purpose is to
 * let you type a question it reads as a gate. The motif is kept exactly —
 * lowercase wordmark, wide tracking, a shimmer travelling letter by letter on
 * the site's 0.09s stagger, then a short lift and fade — and the duration is
 * cut to about a second.
 *
 * `onDone` fires when the lift *starts*, not when it ends, so the hero's own
 * entrance cascade overlaps the curtain's exit rather than queueing behind it.
 * Sequential would total 1.85s of waiting; overlapped it is about 1.1s, and the
 * two easings (accelerating out, decelerating in) read as one movement.
 */
export function Curtain({ onDone }: { onDone: () => void }) {
  const [skip] = useState(shouldSkip);
  const [leaving, setLeaving] = useState(false);
  const [gone, setGone] = useState(false);

  useEffect(() => {
    if (skip) {
      onDone();
      setGone(true);
      return;
    }

    const lift = window.setTimeout(() => {
      setLeaving(true);
      onDone();
    }, HOLD_MS);

    const remove = window.setTimeout(() => setGone(true), HOLD_MS + LIFT_MS);

    return () => {
      window.clearTimeout(lift);
      window.clearTimeout(remove);
    };
  }, [skip, onDone]);

  if (gone) return null;

  return (
    <div
      className={`curtain ${leaving ? "curtain--leaving" : ""}`}
      // Decorative, and the page behind it is already in the accessibility tree.
      aria-hidden="true"
      // Stops the overlay from swallowing a click during its exit, so someone
      // who reaches for the composer early is never blocked by it.
      style={leaving ? { pointerEvents: "none" } : undefined}
    >
      <div className="curtain__word">
        {Array.from(WORDMARK).map((char, index) => (
          <span
            key={`${char}-${index}`}
            className="curtain__letter"
            style={{ "--i": index } as CSSProperties}
          >
            {/* A space cannot carry a visible opacity animation, so it is
                rendered as a fixed gap that keeps the letter indices — and
                therefore the stagger — continuous across the two words. */}
            {char === " " ? " " : char}
          </span>
        ))}
      </div>
    </div>
  );
}
